import {
  BrowserRouter,
  useLocation,
  useNavigate,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom'
import {
  useEffect,
  useState,
  createContext,
  useContext,
  useCallback,
  lazy,
  Suspense,
  useLayoutEffect,
} from 'react'
import { Toaster } from 'react-hot-toast'

import { translations } from './lib/i18n'
import api from './lib/api'
import {
  finishInitialLoading,
} from './context/LoadingContext'

import Navbar from './components/Navbar'
import Footer from './components/Footer'
import Seo from './components/Seo'
import AdminLayout from './admin/AdminLayout'
import { removeInitialPreloader } from './lib/initialPreloader'
import Home from './pages/Home'
import {
  DEFAULT_PUBLIC_LOCALE,
  PUBLIC_LOCALES,
  localizePath,
} from './lib/routing'

const About = lazy(() => import('./pages/About'))
const News = lazy(() => import('./pages/News'))
const Events = lazy(() => import('./pages/Events'))
const Fields = lazy(() => import('./pages/Fields'))
const HeritageLive = lazy(() => import('./pages/HeritageLive'))
const Contact = lazy(() => import('./pages/Contact'))
const PhotoGallery = lazy(() => import('./pages/PhotoGallery'))
const VideoGallery = lazy(() => import('./pages/VideoGallery'))
const EventCollections = lazy(() => import('./pages/EventCollections'))
const NotFound = lazy(() => import('./pages/NotFound'))

const AdminLogin = lazy(() => import('./admin/AdminLogin'))
const Dashboard = lazy(() => import('./admin/Dashboard'))
const ActivityLog = lazy(() => import('./admin/ActivityLog'))
const ManageNews = lazy(() => import('./admin/ManageNews'))
const ManageEvents = lazy(() => import('./admin/ManageEvents'))
const ManageHeritage = lazy(() => import('./admin/ManageHeritage'))
const ManageAdmins = lazy(() => import('./admin/ManageAdmins'))
const PermissionsManagement = lazy(() => import('./admin/PermissionsManagement'))
const ManageMessages = lazy(() => import('./admin/ManageMessages'))
const Profile = lazy(() => import('./admin/Profile'))
const ManagePartners = lazy(() => import('./admin/ManagePartners'))
const ManageHero = lazy(() => import('./admin/ManageHero'))
const ManageSettings = lazy(() => import('./admin/ManageSettings'))
const ManageGallery = lazy(() => import('./admin/ManageGallery'))
const ManageFields = lazy(() => import('./admin/ManageFields'))

export const AppContext = createContext(null)

export const useLang = () => useContext(AppContext)

const DEFAULT_SETTINGS = {
  site_name_ar: 'منظمة تراث اليمن لأجل السلام',
  site_name_en: 'Yemen Heritage for Peace Organization',
  logo_url: '/logo.png',
  contact_phone: '',
  contact_email: '',
  address_ar: '',
  address_en: '',
  home_about_image_url: '',
  home_about_image_alt_ar: '',
  home_about_image_alt_en: '',
}

function getRememberedLang() {
  const savedLang = localStorage.getItem('yhpo_lang')

  return PUBLIC_LOCALES.includes(savedLang)
    ? savedLang
    : DEFAULT_PUBLIC_LOCALE
}

function normalizeSettings(value) {
  const loadedSettings =
    value &&
    typeof value === 'object' &&
    !Array.isArray(value)
      ? value
      : {}

  return {
    ...DEFAULT_SETTINGS,
    ...loadedSettings,
  }
}

import { PublicLoaderProvider } from './context/PublicLoaderContext'

function PublicLayout({ lang }) {
  const location = useLocation()

  return (
    <PublicLoaderProvider>
      <Navbar />

      <Routes>
        <Route index element={<Home />} />
        <Route path="about" element={<About />} />
        <Route path="news" element={<News />} />
        <Route path="news/:slug" element={<News />} />
        <Route path="events" element={<Events />} />
        <Route path="events/:slug" element={<Events />} />

        <Route
          path="events/:eventId/collections"
          element={<EventCollections />}
        />

        <Route path="fields" element={<Fields />} />

        <Route
          path="heritage-life"
          element={<HeritageLive />}
        />
        <Route
          path="heritage-life/:slug"
          element={<HeritageLive />}
        />

        <Route path="contact" element={<Contact />} />

        <Route
          path="photo-gallery"
          element={<PhotoGallery />}
        />

        <Route
          path="video-gallery"
          element={<VideoGallery />}
        />

        <Route
          path="*"
          element={<NotFound />}
        />
      </Routes>

      <Footer />
    </PublicLoaderProvider>
  )
}

function RootRedirect() {
  return <Navigate to={`/${getRememberedLang()}`} replace />
}

function InvalidPublicPathRedirect() {
  const location = useLocation()
  const path = location.pathname.replace(/^\//, '')
  const segments = path.split('/')
  const targetPath = segments[0].length <= 3 && segments.length > 1
    ? `/${segments.slice(1).join('/')}`
    : `/${path}`

  return (
    <Navigate
      to={localizePath(targetPath, getRememberedLang()) + location.search}
      replace
    />
  )
}

function PublicRoute() {
  const { pathname } = useLocation()
  const locale = pathname.split('/')[1]

  if (!PUBLIC_LOCALES.includes(locale)) {
    return <InvalidPublicPathRedirect />
  }

  return <PublicLayout lang={locale} />
}

function AppToaster() {
  return (
    <Toaster
      position="top-center"
      gutter={10}
      toastOptions={{
        duration: 3500,
        style: {
          borderRadius: '12px',
          fontSize: '14px',
          fontWeight: '600',
          padding: '14px 18px',
          maxWidth: '420px',
          color: '#ffffff',
          boxShadow:
            '0 10px 30px rgba(0, 0, 0, 0.22)',
        },
        success: {
          duration: 3000,
          style: {
            background: '#166534',
            color: '#ffffff',
            border: '1px solid #15803d',
            borderRadius: '12px',
            fontSize: '14px',
            fontWeight: '600',
            padding: '14px 18px',
            maxWidth: '420px',
            boxShadow:
              '0 10px 30px rgba(22, 101, 52, 0.28)',
          },
          iconTheme: {
            primary: '#ffffff',
            secondary: '#166534',
          },
        },
        error: {
          duration: 4500,
          style: {
            background: '#7f1d1d',
            color: '#ffffff',
            border: '1px solid #991b1b',
            borderRadius: '12px',
            fontSize: '14px',
            fontWeight: '600',
            padding: '14px 18px',
            maxWidth: '420px',
            boxShadow:
              '0 10px 30px rgba(127, 29, 29, 0.28)',
          },
          iconTheme: {
            primary: '#ffffff',
            secondary: '#7f1d1d',
          },
        },
      }}
    />
  )
}

function InitialPreloaderHandoff() {
  useLayoutEffect(() => {
    removeInitialPreloader()
  }, [])

  return null
}

function RoutedApp({
  settings,
  settingsLoading,
  settingsError,
  refreshSettings,
}) {
  const location = useLocation()
  const navigate = useNavigate()
  const pathLocale = location.pathname.split('/')[1]
  const lang = PUBLIC_LOCALES.includes(pathLocale)
    ? pathLocale
    : getRememberedLang()
  const t = translations[lang]
  const dir = lang === 'ar' ? 'rtl' : 'ltr'

  useEffect(() => {
    document.documentElement.lang = lang
    document.documentElement.dir = dir
    document.body.dir = dir
  }, [lang, dir])

  const toggleLang = useCallback(() => {
    const nextLang = lang === 'ar' ? 'en' : 'ar'
    localStorage.setItem('yhpo_lang', nextLang)

    const nextPath = location.pathname.replace(
      /^\/(ar|en)(?=\/|$)/,
      ''
    ) || '/'

    navigate(`${localizePath(nextPath, nextLang)}${location.search}`)
  }, [lang, location.pathname, location.search, navigate])

  const [seoData, setSeoData] = useState(null)

  const contextValue = {
    lang,
    t,
    dir,
    toggleLang,
    settings,
    settingsLoading,
    settingsError,
    refreshSettings,
    bootLoading: false,
    bootError: null,
    retryBootstrap: refreshSettings,
    seoData,
    setSeoData,
  }

  // Clear SEO data on navigation to avoid showing previous page's SEO temporarily
  useEffect(() => {
    setSeoData(null)
  }, [location.pathname])

  return (
    <AppContext.Provider value={contextValue}>
      <div
        dir={dir}
        className={
          lang === 'ar'
            ? 'font-ar'
            : 'font-en'
        }
      >
        <InitialPreloaderHandoff />
        <Seo lang={lang} />
        <AppToaster />

        <Suspense fallback={null}>
          <Routes>
              <Route
                path="/admin/login"
                element={<AdminLogin />}
              />

              <Route
                path="/admin/*"
                element={<AdminLayout />}
              >
              <Route
                index
                element={<Dashboard />}
              />

              <Route
                path="activity-log"
                element={<ActivityLog />}
              />

              <Route
                path="news"
                element={<ManageNews />}
              />

              <Route
                path="events"
                element={<ManageEvents />}
              />

              <Route
                path="heritage"
                element={<ManageHeritage />}
              />

              <Route
                path="partners"
                element={<ManagePartners />}
              />

              <Route
                path="hero"
                element={<ManageHero />}
              />

              <Route
                path="settings"
                element={<ManageSettings />}
              />

              <Route
                path="admins"
                element={<ManageAdmins />}
              />

              <Route
                path="permissions"
                element={<PermissionsManagement />}
              />

              <Route
                path="messages"
                element={<ManageMessages />}
              />

              <Route
                path="profile"
                element={<Profile />}
              />

              <Route
                path="gallery"
                element={<ManageGallery />}
              />

              <Route
                path="fields-config"
                element={<ManageFields />}
              />

              <Route
                path="*"
                element={
                  <Navigate
                    to="/admin"
                    replace
                  />
                }
              />
            </Route>

            <Route path="/" element={<RootRedirect />} />
            <Route path="/:locale/*" element={<PublicRoute />} />
            <Route path="*" element={<InvalidPublicPathRedirect />} />
          </Routes>
        </Suspense>

      </div>
    </AppContext.Provider>
  )
}

export default function App() {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS)
  const [settingsLoading, setSettingsLoading] = useState(true)
  const [settingsError, setSettingsError] = useState(null)

  const fetchSettings = useCallback(async () => {
    const siteSettings = await api.get('/settings', {
      globalLoading: false,
      loadingLabel: 'site-settings',
    })

    return normalizeSettings(siteSettings)
  }, [])

  const refreshSettings = useCallback(async () => {
    setSettingsLoading(true)
    setSettingsError(null)

    try {
      const loadedSettings = await fetchSettings()
      setSettings(loadedSettings)
      return loadedSettings
    } catch (error) {
      console.error('Failed to refresh site settings:', error)
      setSettingsError(error)
      throw error
    } finally {
      setSettingsLoading(false)
    }
  }, [fetchSettings])

  useEffect(() => {
    let cancelled = false

    finishInitialLoading()
    setSettingsLoading(true)
    setSettingsError(null)

    fetchSettings()
      .then((loadedSettings) => {
        if (cancelled) return
        setSettings(loadedSettings)
        setSettingsError(null)
      })
      .catch((error) => {
        if (cancelled) return
        console.error('Failed to load site settings in the background:', error)
        setSettingsError(error)
      })
      .finally(() => {
        if (!cancelled) setSettingsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [fetchSettings])

  return (
    <BrowserRouter>
      <RoutedApp
        settings={settings}
        settingsLoading={settingsLoading}
        settingsError={settingsError}
        refreshSettings={refreshSettings}
      />
    </BrowserRouter>
  )
}
